import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@Sage/hoi-poi-ui';
import { useSelector } from 'react-redux';
import CrudField from 'containers/components/EntityCrud/CrudField';
import MultipleCrudField from 'containers/components/EntityCrud/MultipleCrudField';
import { SALESORDERS } from 'constants/Entities';
import { getSmartFieldProps } from 'utils/fm/fields';
import { getLiteral } from 'utils/getLiteral';
import { isSageImplementation } from 'utils/sage';

import './styles.scss';

const DISCOUNT_PERCENT_FIELDS = [
    { fieldId: 'discount1', labelKey: 'label_salesorder_discount1' },
    { fieldId: 'discount2', labelKey: 'label_salesorder_discount2' },
    { fieldId: 'discount3', labelKey: 'label_salesorder_discount3' },
    { fieldId: 'discount4', labelKey: 'label_salesorder_discount4' },
];

const MAX_ORDER_DISCOUNTS = DISCOUNT_PERCENT_FIELDS.length;

const ORDER_DISCOUNT_PERCENT_INPUT_ATTRS = {
    preComponent: <span className="sales-order-discounts-crud-tab__symbol">%</span>,
};

const getInitialVisibleDiscountRowCount = (orderData, sageOnlyFirstDiscount) => {
    const fields = sageOnlyFirstDiscount
        ? DISCOUNT_PERCENT_FIELDS.slice(0, 1)
        : DISCOUNT_PERCENT_FIELDS;
    let maxFilledIndex = -1;
    fields.forEach(({ fieldId }, index) => {
        const valueFieldId = `${fieldId}Value`;
        if (orderData?.[fieldId] || orderData?.[valueFieldId]) maxFilledIndex = index;
    });
    return Math.max(maxFilledIndex + 1, 1);
};

const Discounts = memo(
    ({
        changeField,
        configMap,
        data,
        entityName,
        environmentField,
        errors,
        firstErrorField,
        getError,
        idCurrency,
        idEnvironment,
        isBulkAction,
        isNewEntity,
    }) => {
        const standardFieldsSchemaSalesOrders = useSelector(
            (state) => state.config?.standardFieldsSchemaMap?.[SALESORDERS.extraFieldName],
        );

        const salesOrderAmountDiscounts = useSelector(
            (state) => !!state.config?.userData?.salesOrderAmountDiscounts,
        );

        const sageOnlyFirstDiscount = useSelector((state) =>
            isSageImplementation(state.config?.productModeCode),
        );
        const maxDiscountRows = sageOnlyFirstDiscount ? 1 : MAX_ORDER_DISCOUNTS;

        const [visibleRowCount, setVisibleRowCount] = useState(() =>
            Math.min(
                getInitialVisibleDiscountRowCount(data, sageOnlyFirstDiscount),
                maxDiscountRows,
            ),
        );

        useEffect(() => {
            const requiredRows = Math.min(
                getInitialVisibleDiscountRowCount(data, sageOnlyFirstDiscount),
                maxDiscountRows,
            );
            setVisibleRowCount((prev) => Math.max(prev, requiredRows));
        }, [data, sageOnlyFirstDiscount, maxDiscountRows]);

        const disableEditSalesOrdersLines = useMemo(() => {
            const smartFieldProps = getSmartFieldProps({
                field: standardFieldsSchemaSalesOrders?.addProductsButton,
                configMap,
                data,
                isNewEntity,
            });
            return Object.keys(smartFieldProps).length ? !!smartFieldProps.hidden : false;
        }, [data, isNewEntity, configMap, standardFieldsSchemaSalesOrders?.addProductsButton]);

        const addDiscountRow = useCallback(() => {
            setVisibleRowCount((n) => Math.min(n + 1, maxDiscountRows));
        }, [maxDiscountRows]);

        const currencySymbol = data?.currencySymbol ?? '';

        const rowOnChange = useCallback(
            (percentFieldId, valueFieldId) => (subFieldId) => (rawValue) => {
                const value = rawValue === '' ? null : rawValue;
                if (salesOrderAmountDiscounts && value === null) {
                    changeField()({
                        [percentFieldId]: null,
                        [valueFieldId]: null,
                    });
                    return;
                }
                changeField(subFieldId)(rawValue);
            },
            [changeField, salesOrderAmountDiscounts],
        );

        return (
            <div className="sales-order-discounts-crud-tab">
                <div className="sales-order-discounts-crud-tab__content">
                    <div className="sales-order-discounts-crud-tab__rows">
                        {DISCOUNT_PERCENT_FIELDS.slice(
                            0,
                            Math.min(visibleRowCount, maxDiscountRows),
                        ).map(({ fieldId, labelKey }) => {
                            const fieldSchema = standardFieldsSchemaSalesOrders?.[fieldId];
                            const smartFieldOverrides = isBulkAction
                                ? {}
                                : getSmartFieldProps({
                                      field: fieldSchema,
                                      configMap,
                                      data,
                                      isNewEntity,
                                  });
                            const { defaultValue: _defaultValue, ...fieldOverrides } =
                                smartFieldOverrides;
                            const hidden = Object.prototype.hasOwnProperty.call(
                                fieldOverrides,
                                'hidden',
                            )
                                ? fieldOverrides.hidden
                                : fieldSchema?.hidden;
                            const readOnly =
                                fieldOverrides.readOnly ?? fieldSchema?.readOnly ?? false;
                            const mandatory = isBulkAction
                                ? false
                                : (fieldOverrides.mandatory ?? fieldSchema?.mandatory ?? false);

                            if (hidden) return null;

                            const valueFieldId = `${fieldId}Value`;
                            const discountDecimalType = fieldSchema?.inputAttrs?.decimalType;
                            const valueInputAttrs = {
                                preComponent: (
                                    <span className="sales-order-discounts-crud-tab__symbol">
                                        {currencySymbol}
                                    </span>
                                ),
                                ...(discountDecimalType != null
                                    ? { decimalType: discountDecimalType }
                                    : {}),
                            };

                            if (salesOrderAmountDiscounts) {
                                const inputs = [
                                    {
                                        id: fieldId,
                                        fieldConfiguration: fieldId,
                                        type: 'decimal',
                                        readOnly,
                                        hidden,
                                        mandatory,
                                        inputAttrs: ORDER_DISCOUNT_PERCENT_INPUT_ATTRS,
                                    },
                                    {
                                        id: valueFieldId,
                                        fieldConfiguration: fieldId,
                                        type: 'decimal',
                                        readOnly,
                                        hidden,
                                        mandatory,
                                        inputAttrs: valueInputAttrs,
                                    },
                                ];

                                return (
                                    <div
                                        key={fieldId}
                                        className="sales-order-discounts-crud-tab__row sales-order-discounts-crud-tab__row--grouped"
                                    >
                                        <MultipleCrudField
                                            configMap={configMap}
                                            data={data ?? {}}
                                            entityName={entityName}
                                            environmentField={environmentField}
                                            errors={errors ?? {}}
                                            fieldsMode="horizontal"
                                            firstErrorField={firstErrorField}
                                            getError={getError}
                                            idCurrency={idCurrency}
                                            idEnvironment={idEnvironment}
                                            inputs={inputs}
                                            isBulkAction={isBulkAction}
                                            label={getLiteral(labelKey)}
                                            labelMode="horizontal"
                                            onChange={rowOnChange(fieldId, valueFieldId)}
                                        />
                                    </div>
                                );
                            }

                            const onChange = changeField(fieldId);
                            const onClear = () => onChange(null);

                            const crudFieldProps = {
                                canClear: !readOnly && !mandatory,
                                entityName,
                                environmentField,
                                error: getError(errors?.[fieldId], []),
                                fieldConfiguration: fieldId,
                                fieldId,
                                firstErrorField:
                                    !!firstErrorField && fieldId.toLowerCase() === firstErrorField,
                                idCurrency,
                                idEnvironment,
                                inputAttrs: ORDER_DISCOUNT_PERCENT_INPUT_ATTRS,
                                isBulkAction,
                                isNewEntity,
                                label: getLiteral(labelKey),
                                labelMode: 'horizontal',
                                mandatory,
                                onChange,
                                onClear,
                                readOnly,
                                type: 'decimal',
                                value: data[fieldId],
                                ...fieldOverrides,
                            };

                            return (
                                <div key={fieldId} className="sales-order-discounts-crud-tab__row">
                                    <CrudField {...crudFieldProps} />
                                </div>
                            );
                        })}
                        {visibleRowCount < maxDiscountRows && (
                            <div className="sales-order-discounts-crud-tab__add-discount">
                                <Button
                                    isDisabled={disableEditSalesOrdersLines}
                                    onClick={addDiscountRow}
                                    type="terciary"
                                >
                                    {getLiteral('action_add_discount')}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    },
);

Discounts.displayName = 'Discounts';

export default Discounts;
